import type { BrowserProductEvent } from "./productTelemetry.js";
import { buildBrowserProductEvent, enqueueBrowserProductEvent } from "./productTelemetry.js";

// Our first-party event source is independent of PostHog's SDK.
export type PostHogCapture = (event: BrowserProductEvent | null, properties: Record<string, unknown>, name: string) => Promise<boolean>;
type DeliveryOutcome = {
    productQueued: boolean;
    posthogSent: boolean;
};
let posthogCapture: PostHogCapture | undefined;
let productDestinationEnabled = false;
let inMemorySessionId: string | undefined;
const SESSION_KEY = "dc.browser.analytics.session.v1";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function configureBrowserAnalytics(enabled: boolean): void {
    productDestinationEnabled = enabled;
}

/** PostHog stays optional, but no other analytics behavior depends on it. */
export function configurePostHogCapture(capture?: PostHogCapture): void {
    posthogCapture = capture;
}

/** Kept for the existing synthetic and integration tests. */
export const registerPostHogDestination = configurePostHogCapture;

export function browserSessionId(): string {
    if (inMemorySessionId) {
        return inMemorySessionId;
    }
    if (typeof window === "undefined") {
        return crypto.randomUUID();
    }
    try {
        const saved = window.sessionStorage.getItem(SESSION_KEY);
        if (saved && UUID.test(saved)) {
            return (inMemorySessionId = saved);
        }
    } catch {
        /* Private-browsing storage may be unavailable. */
    }
    const generated = crypto.randomUUID();
    inMemorySessionId = generated;
    try {
        window.sessionStorage.setItem(SESSION_KEY, generated);
    } catch {
        /* Use memory. */
    }
    return generated;
}

/** Privacy-safe browser family, never the raw user-agent string. */
export function browserFamily(ua: string): string {
    if (/Edg(?:\/|A\/|iOS\/)/i.test(ua)) {
        return "edge";
    }
    if (/(?:OPR|OPiOS)\//i.test(ua)) {
        return "opera";
    }
    if (/(?:Firefox|FxiOS)\//i.test(ua)) {
        return "firefox";
    }
    if (/Chrome\//i.test(ua) || /CriOS\//i.test(ua)) {
        return "chrome";
    }
    if (/Safari\//i.test(ua)) {
        return "safari";
    }
    return "other";
}

/** Coarse browser metadata; do not retain the full user-agent string. */
export function browserDetails(ua: string): {
    browser: string;
    browser_major_version: string;
    browser_os: string;
} {
    const browser = browserFamily(ua);
    const versionPattern: Record<string, RegExp> = {
        edge: /Edg(?:e|A|iOS)?\/(\d+)/i,
        opera: /(?:OPR|OPiOS)\/(\d+)/i,
        firefox: /(?:Firefox|FxiOS)\/(\d+)/i,
        chrome: /(?:Chrome|CriOS)\/(\d+)/i,
        safari: /Version\/(\d+)/i,
    };
    const browser_major_version = versionPattern[browser]?.exec(ua)?.[1] ?? "unknown";
    const browser_os = /Windows/i.test(ua)
        ? "windows"
        : /(?:iPhone|iPad|iPod)/i.test(ua)
          ? "ios"
          : /(?:Android)/i.test(ua)
            ? "android"
            : /(?:Macintosh|Mac OS X)/i.test(ua)
              ? "mac"
              : /Linux/i.test(ua)
                ? "linux"
                : "other";
    return {
        browser,
        browser_major_version,
        browser_os,
    };
}

export function createBrowserAnalyticsEvent(name: string, properties: Record<string, unknown> = {}): BrowserProductEvent | null {
    if (typeof window === "undefined") {
        return null;
    }
    try {
        const current = window.location;
        return buildBrowserProductEvent(
            name,
            {
                ...properties,
                ...browserDetails(typeof navigator !== "undefined" ? navigator.userAgent : ""),
            },
            {
                eventId: crypto.randomUUID(),
                timestamp: new Date().toISOString(),
                browserSessionId: browserSessionId(),
                hostname: current.hostname,
                pathname: current.pathname,
                search: current.search,
                referrer: typeof document !== "undefined" ? document.referrer : "",
            },
        );
    } catch {
        // Failure of the first-party SDK must not prevent the original PostHog capture.
        return null;
    }
}

/** Separate results: successful BigQuery admission is never PostHog success. */
export async function dispatchBrowserAnalyticsWithOutcomes(
    event: BrowserProductEvent,
    rawProperties: Record<string, unknown>,
    productSink: (event: BrowserProductEvent) => boolean,
    posthogSink?: PostHogCapture,
): Promise<DeliveryOutcome> {
    let productQueued = false;
    try {
        productQueued = productSink(event);
    } catch {
        /* Independent destinations. */
    }
    let posthogSent = false;
    try {
        posthogSent = !!(await posthogSink?.(event, rawProperties, event.name));
    } catch {
        /* Independent destinations. */
    }
    return {
        productQueued,
        posthogSent,
    };
}

/** Retain the old boolean API for normal events; use outcome details for deduplication. */
export async function dispatchBrowserAnalytics(
    event: BrowserProductEvent,
    rawProperties: Record<string, unknown>,
    productSink: (event: BrowserProductEvent) => boolean,
    posthogSink?: PostHogCapture,
): Promise<boolean> {
    const status = await dispatchBrowserAnalyticsWithOutcomes(event, rawProperties, productSink, posthogSink);
    return status.productQueued || status.posthogSent;
}

export async function trackEvent(name: string, properties: Record<string, unknown> = {}): Promise<boolean> {
    const event = createBrowserAnalyticsEvent(name, properties);
    if (!event) {
        // Even if the new analytics event cannot be constructed, preserve
        // PostHog's previous ability to capture the raw custom event.
        try {
            return !!(await posthogCapture?.(null, properties, name));
        } catch {
            return false;
        }
    }
    return dispatchBrowserAnalytics(event, properties, productDestinationEnabled ? enqueueBrowserProductEvent : () => false, posthogCapture);
}

/**
 * Build and enqueue a navigation-triggered event synchronously.
 * Redirecting in this JS task would otherwise interrupt PostHog's async adapter.
 * The navigation caller sends the *same* event ID to PostHog immediately.
 */
export function trackNavigationEvent(name: string, properties: Record<string, unknown> = {}): BrowserProductEvent | null {
    const event = createBrowserAnalyticsEvent(name, properties);
    if (event && productDestinationEnabled) {
        try {
            enqueueBrowserProductEvent(event);
        } catch {
            /* Never block auth navigation. */
        }
    }
    return event;
}

/** Only the first-party side of pageviews. PostHog pageviews use its original capture call. */
export function trackPageview(): BrowserProductEvent | null {
    const event = createBrowserAnalyticsEvent("$pageview");
    if (event && productDestinationEnabled) {
        enqueueBrowserProductEvent(event);
    }
    return event;
}

/** PostHog URL semantics are unchanged: preserve search parameters and omit #hash. */
export function legacyPostHogPageviewUrl(location: Pick<Location, "origin" | "pathname" | "search">): string {
    return location.origin + location.pathname + location.search;
}

/**
 * Deduplicate separately per destination. Accepting into the BigQuery queue
 * cannot mark a funnel event as delivered to PostHog.
 */
const posthogOnce = new Set<string>();
const productOnce = new Set<string>();
const sendingOnce = new Map<string, Promise<void>>();

function sessionGet(key: string): string | null {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}
function sessionSet(key: string, value: string): void {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        /* Memory-only fallback. */
    }
}

export async function trackEventOnce(name: string, key: string, properties: Record<string, unknown> = {}): Promise<void> {
    if (typeof window === "undefined") {
        return;
    }
    const phKey = "ph.once." + name + "." + key;
    const bqKey = "bq.once." + name + "." + key;
    const phDone = posthogOnce.has(phKey) || sessionGet(phKey) === "1";
    const bqSaved = sessionGet(bqKey);
    const bqDone = productOnce.has(bqKey) || bqSaved !== null;
    if ((phDone || !posthogCapture) && (bqDone || !productDestinationEnabled)) {
        return;
    }
    if (sendingOnce.has(phKey)) {
        await sendingOnce.get(phKey);
        return;
    }

    const pending = (async () => {
        const rawProperties = {
            ...properties,
            dedupe_scope: "tab",
        };
        const event = createBrowserAnalyticsEvent(name, rawProperties);
        if (!event) {
            let sent = false;
            try {
                sent = !!(await (!phDone && posthogCapture ? posthogCapture(null, rawProperties, name) : false));
            } catch {
                /* Keep the PostHog once-key available for retry. */
            }
            if (sent) {
                posthogOnce.add(phKey);
                sessionSet(phKey, "1");
            }
            return;
        }
        // Preserve the event identity when retrying PostHog after a reload.
        if (bqSaved) {
            try {
                const old = JSON.parse(bqSaved) as {
                    eventId?: string;
                    timestamp?: string;
                };
                if (old.eventId && UUID.test(old.eventId)) {
                    event.event_id = old.eventId;
                }
                if (old.timestamp && Number.isFinite(Date.parse(old.timestamp))) {
                    event.occurred_at = old.timestamp;
                }
            } catch {
                /* The key might come from a legacy format. */
            }
        }
        const outcome = await dispatchBrowserAnalyticsWithOutcomes(
            event,
            rawProperties,
            productDestinationEnabled && !bqDone ? enqueueBrowserProductEvent : () => false,
            !phDone ? posthogCapture : undefined,
        );
        if (outcome.productQueued) {
            productOnce.add(bqKey);
            sessionSet(
                bqKey,
                JSON.stringify({
                    eventId: event.event_id,
                    timestamp: event.occurred_at,
                }),
            );
        }
        if (outcome.posthogSent) {
            posthogOnce.add(phKey);
            sessionSet(phKey, "1");
        }
    })().finally(() => sendingOnce.delete(phKey));
    sendingOnce.set(phKey, pending);
    await pending;
}
